import { useTranslation } from 'react-i18next';

import { Button } from '../ui/button';

import type { ReactNode } from 'react';

/**
 * How a sensor block presents the number it has, and the control that commits it.
 *
 * GPS, Compass and Tilt had each answered the same three questions separately, and had each
 * answered them differently:
 *
 *   BEFORE A CAPTURE   GPS showed dashes, Compass showed an invisible spacer, Tilt showed the live
 *                      value in a lighter weight.
 *   AFTER ONE          GPS and Compass looked exactly as they had before, so nothing on screen said
 *                      the number was now the student's answer; Tilt went bold.
 *   PRESSING AGAIN     GPS and Compass re-captured under an unchanged label; Tilt said "Capture
 *                      Again".
 *
 * None of it was wrong in isolation. Together it meant the same gesture looked like three gestures.
 *
 * ## The rule
 *
 *   LIVE MODE                    bold, full contrast. It updates forever and it IS the answer.
 *   CAPTURE MODE, nothing yet    bold, muted. The instrument is working and you can watch it; this
 *                                number is not yours until you take it.
 *   CAPTURE MODE, captured       bold, full contrast. Frozen, and now the answer.
 *   NO READING OBTAINABLE        dashes, in any mode. A different state with its own advisory.
 *
 * Weight is constant and only COLOUR moves, so committing a reading cannot reflow the line. The
 * muted step is what the old placeholders had no room for: dashes say "this instrument is not
 * working", which is a claim about the device, where a muted live number says "not yet", which is
 * a claim about the student. Conflating them left a student unable to tell a dead sensor from an
 * unpressed button until after they had committed.
 */
export const SENSOR_READOUT_PROVISIONAL = 'font-semibold text-ws-muted';
export const SENSOR_READOUT_COMMITTED = 'font-semibold text-ws-text';

/** The tone half of a readout's classes. The caller still owns its own type scale — GPS sets a
 *  two-line coordinate at `text-4xl` where a single angle carries `text-5xl`. */
export function sensorReadoutTone(committed: boolean): string {
  return committed ? SENSOR_READOUT_COMMITTED : SENSOR_READOUT_PROVISIONAL;
}

/**
 * Capture, and then Reset — never "Capture Again".
 *
 * "Capture Again" commits whatever the instrument says at the instant it is pressed, while the
 * screen is still showing the FROZEN earlier number. A student who misread their first capture
 * presses it and the answer jumps to a value they never saw: a blind commit, which is a worse
 * failure than the misreading it was meant to repair.
 *
 * Reset instead clears the committed answer and puts the live readout back, so the second reading
 * costs the same two deliberate acts as the first — watch, then take. It also has to clear the
 * ANSWER and not merely the display: leaving the old answer graded while a live number moves on
 * screen would show the student one value and mark another, which is the exact confusion freezing
 * on capture exists to prevent, inverted.
 *
 * Reset is `secondary` rather than the primary fill. The filled button is the one that records a
 * measurement; letting an undo wear the same colour made the two indistinguishable at a glance in
 * the only mode where both appear.
 *
 * Only Capture mode has any button at all — Live Reading has nothing to press by definition, and
 * Capture on Submit reads the device at submission — so this component is never asked to represent
 * those modes.
 */
export function SensorCaptureButton({
  committed, captureLabel, icon, onCapture, onReset, disabled,
}: {
  /** Whether there is a committed answer right now, from a capture or a typed bypass. */
  committed: boolean;
  /** Per block, because each names its own measurement: "Capture Location", "Capture Tilt". */
  captureLabel: string;
  /** The block's own mark. Sized and hidden from assistive tech here, so callers pass it bare and
   *  no icon component has to grow an `aria-hidden` prop of its own to be used in a button. */
  icon: ReactNode;
  onCapture?: () => void;
  onReset?: () => void;
  /** The unselected card, where this renders as an inert span — see `Button`'s `as`. */
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  // The reset label is shared across all three blocks, unlike the capture label: there is nothing
  // block-specific about discarding a reading, and three spellings of "Reset" would be three.
  const label = committed ? t('blocks.sensorSource.resetButton') : captureLabel;
  return (
    <Button
      as={disabled ? 'span' : 'button'}
      disabled={disabled}
      variant={committed ? 'secondary' : 'primary'}
      size='lg'
      icon={<span aria-hidden='true' className='contents'>{icon}</span>}
      onClick={committed ? onReset : onCapture}
    >
      {label}
    </Button>
  );
}

/**
 * The one thing a screen reader is told when a reading lands, and the only live region on a
 * sensor block's readout.
 *
 * The readout itself must NOT be one. Compass's live mode and Tilt's pre-capture readout both had
 * `aria-live` sitting directly on a continuously updating number, which queues one polite
 * announcement per sensor sample — a reader is left reciting the instrument instead of the page,
 * and arrowing away does not stop it.
 *
 * A capture still has to be announced, because it is a number appearing with no focus move. So the
 * announcement is separated from the display: this region is silent while a value is merely live,
 * and speaks once at the moment a capture commits one. Live Reading therefore says nothing here at
 * all — it has no commit, only a stream, and a reader gets its value by arrowing to the readout.
 * Nor does a typed bypass, whose own input announces itself as it is typed. Clearing it on reset emits nothing — an emptied
 * live region is not announced — which is correct, because the button's own label has changed back
 * to Capture under the focus that pressed it and has already said so.
 */
export function SensorAnnounce({ value }: {
  /**
   * The reading a CAPTURE just committed, formatted exactly as the visible readout formats it — a node rather
   * than a string, because Compass's cardinal formats carry markup and re-flattening them here
   * would be a second spelling of the same heading, free to drift from the one on screen.
   *
   * Null while nothing is committed.
   */
  value?: ReactNode;
}) {
  return <span aria-live='polite' aria-atomic='true' className='sr-only'>{value ?? ''}</span>;
}
